/**
 * DocService — DOC-1 Moat Zero write path.
 *
 * Single entry point for every agent decision, event emission, human override,
 * external integration call, and system event. No code writes to doc_entries
 * except through this service.
 *
 * Storage layout:  doc_entries/{entryId}   (ULID document ID)
 * Narratives:      doc_narratives/{entryId} (generated async by Cloud Function)
 * Rejections:      doc_rejections/{auto}    (invalid entries — never lost)
 *
 * Architecture rules:
 *   • DataService.setDoc is the only Firestore write path.
 *   • tenantId required on every call.
 *   • Entries are append-only — never updated, only superseded.
 *   • Every entry is signed. An unsigned entry is a corrupt entry.
 *   • Narrative generation is async — write path never blocks on it.
 */

import DataService from './DataService';
import { validateDocEntry } from './docSchemas';

// ── ULID (browser-compatible, no external dep) ──────────────────────────────
const B32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function generateULID() {
  const ms = Date.now();
  const t = new Array(10);
  let tmp = ms;
  for (let i = 9; i >= 0; i--) { t[i] = B32[tmp % 32]; tmp = Math.floor(tmp / 32); }
  const r = Array.from({ length: 16 }, () => B32[Math.floor(Math.random() * 32)]);
  return t.join('') + r.join('');
}

// ── Crypto helpers (Web Crypto API) ─────────────────────────────────────────
async function sha256hex(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function hmacHex(message, keyStr) {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw', enc.encode(keyStr), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
  return Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Canonical JSON — excludes signing fields so the hash is stable before signing.
function canonicalJSON(entry) {
  const { signature: _s, signedBy: _sb, narrativeRef: _nr, narrativeStatus: _ns, ...core } = entry;
  void _s; void _sb; void _nr; void _ns;
  return JSON.stringify(core, Object.keys(core).sort());
}

// Signing key derived from tenantId (placeholder for Cloud KMS).
// Step 3 KMS upgrade replaces this with a Cloud KMS API call; verify() mirrors the derivation.
function signingKey(tenantId) {
  return `kairos-doc-signing-v1:${tenantId}`;
}

const DocService = {
  /**
   * Write a doc entry.
   *
   * Required fields:
   *   tenantId     — tenant scope
   *   entryVariant — see docSchemas.ENTRY_VARIANTS
   *   actorId      — agent id, user uid, or 'system'
   *   actorType    — 'agent' | 'user' | 'system' | 'external'
   *   payload      — variant-specific data object
   *
   * agent_decision also requires: confidence (0–1), reasoningChain
   * event_emission also requires: eventTopic
   *
   * Optional:
   *   eventTopic      — ORCH-1 topic (required for event_emission)
   *   parentEntryIds  — causation chain (array of prior entryIds)
   *   inputHashes     — hashes of inputs the agent read
   *   modelVersion    — exact model hash + prompt-template version
   *   reasoningChain  — agent's stepwise reasoning
   *   confidence      — 0 to 1 for agent decisions
   *   attestationRef  — reserved for TRUST-1; pass null
   *
   * Returns the written entry with its entryId.
   * Throws on schema violation (also logs to doc_rejections).
   */
  async write({
    tenantId,
    entryVariant,
    actorId,
    actorType,
    payload = {},
    eventTopic = null,
    parentEntryIds = [],
    inputHashes = [],
    modelVersion = null,
    reasoningChain = null,
    confidence = null,
    attestationRef = null,
  }) {
    if (!tenantId) throw new Error('DocService.write: tenantId required');

    const entryId = generateULID();
    const createdAt = new Date().toISOString();

    const entry = {
      entryId,
      tenantId,
      entryVariant,
      actorId,
      actorType,
      eventTopic,
      parentEntryIds: Array.isArray(parentEntryIds) ? parentEntryIds : [],
      payload,
      inputHashes: Array.isArray(inputHashes) ? inputHashes : [],
      modelVersion,
      reasoningChain,
      confidence: typeof confidence === 'number' ? confidence : null,
      attestationRef,
      narrativeStatus: 'pending',
      narrativeRef: null,
      createdAt,
    };

    // Schema validation — errors route to doc_rejections, never silently written.
    const errors = validateDocEntry(entry);
    if (errors.length > 0) {
      const rejection = {
        tenantId,
        entryId,
        entryVariant,
        actorId,
        errors,
        rejectedPayload: payload,
        rejectedAt: createdAt,
      };
      DataService.create('doc_rejections', rejection, actorId || 'system', 'doc_service').catch(() => {});
      throw new Error(`DocService.write: schema validation failed — ${errors.join('; ')}`);
    }

    // Content-addressing: SHA-256 of canonical JSON (excludes signing fields).
    const canonical = canonicalJSON(entry);
    entry.payloadHash = await sha256hex(canonical);

    // Sign.
    entry.signature = await hmacHex(canonical, signingKey(tenantId));
    entry.signedBy = 'hmac-sha256:v1';

    await DataService.setDoc('doc_entries', entryId, entry);
    return entry;
  },

  /**
   * Verify a previously-written entry has not been tampered with.
   * Returns true if the stored hash and signature match recomputed values.
   * Returns false if the entry is missing or tampered.
   */
  async verify(entryId, tenantId) {
    try {
      const stored = await DataService.getSubById('doc_entries', entryId);
      if (!stored) return false;
      if (stored.tenantId !== tenantId) return false;

      const { id: _id, signature: storedSig, payloadHash: storedHash, ...rest } = stored;
      void _id;
      const canonical = canonicalJSON(rest);

      const expectedHash = await sha256hex(canonical);
      if (expectedHash !== storedHash) return false;

      const expectedSig = await hmacHex(canonical, signingKey(tenantId));
      return expectedSig === storedSig;
    } catch {
      return false;
    }
  },

  /**
   * List recent entries for a tenant.
   * Used by Mission Control and EvidenceView.
   */
  async listEntries(tenantId, { variant, actorId: filterActorId, limit: maxRows = 100 } = {}) {
    if (!tenantId) return [];
    const conditions = [{ field: 'tenantId', op: '==', value: tenantId }];
    if (variant) conditions.push({ field: 'entryVariant', op: '==', value: variant });
    if (filterActorId) conditions.push({ field: 'actorId', op: '==', value: filterActorId });
    try {
      return await DataService.queryFlex('doc_entries', conditions, {
        orderBy: 'createdAt',
        direction: 'desc',
        limit: maxRows,
      });
    } catch {
      return [];
    }
  },

  /**
   * Replay — return all doc_entries associated with a workflowId, ordered
   * causal-ascending. Used by WorkflowDetailDrawer for terminal workflows.
   * For active workflows the Mission Control stream provides live updates.
   *
   * Fetches a broad tenant set and filters client-side by payload.workflowId,
   * avoiding a Firestore composite index requirement.
   */
  async replay(tenantId, workflowId) {
    if (!tenantId || !workflowId) return [];
    try {
      const all = await DataService.queryFlex('doc_entries', [
        { field: 'tenantId', op: '==', value: tenantId },
      ], { orderBy: 'createdAt', direction: 'asc', limit: 500 });
      return all.filter(e =>
        e.payload?.workflowId === workflowId ||
        e.payload?.negotiationId === workflowId
      );
    } catch {
      return [];
    }
  },
};

export default DocService;
